/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { darken } from "polished";
import { Search as SearchIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  InputBase,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";

import { MiraTheme } from "../../theme/theme";
import { DataProject } from "../../../../api/dataprojects";
import { Contact, searchForContacts } from "../../../../api/contacts";
import { SuggestedChangeDetailsModal } from "../../../suggested-changes/RecordDetails";

const SearchContainer = styled.div<{ theme?: MiraTheme }>`
  position: relative;
  display: none;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`;

const SearchBox = styled.div<{ theme?: MiraTheme }>`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  padding-right: ${(props) => props.theme.spacing(2)};
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }
`;

const SearchResultsContainer = styled(Paper)<{ theme?: MiraTheme }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: ${(props) => props.theme.spacing(1)};
  max-height: 400px;
  overflow-y: auto;
  z-index: 1300;
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 0 0 auto;
  min-width: 350px;
`;

const Input = styled(InputBase)<{ theme?: MiraTheme }>`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
  }
`;

const RecentItemsContainer = styled.div<{ theme?: MiraTheme }>`
  display: none;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  ${(props) => props.theme.breakpoints.up("lg")} {
    display: flex;
    justify-content: flex-end;
  }
`;

type SearchResult = {
  id: number;
  name: string;
  type: "data-project" | "contact";
  contact?: Contact;
};

type RecentItem = {
  id: number;
  name: string;
  type: "data-project" | "contact";
  contact?: Contact;
  timestamp: number;
};

const RECENT_ITEMS_KEY = "navbar-search-recent-items";

type NavbarSearchProps = {
  dataProjects?: DataProject[];
  selectedSystemId?: number;
};

export const NavbarSearch: React.FC<NavbarSearchProps> = ({
  dataProjects,
  selectedSystemId,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [isContactsModalOpen, setIsContactsModalOpen] =
    useState<boolean>(false);
  const [selectedContactId, setSelectedContactId] = useState<number>();
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLLIElement>(null);

  // Load recent items from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_ITEMS_KEY);
      if (stored) {
        const items = JSON.parse(stored) as RecentItem[];
        setRecentItems(items);
      }
    } catch (error) {
      console.error("Error loading recent items:", error);
    }
  }, []);

  // Save a recent item to localStorage
  const addRecentItem = (result: SearchResult) => {
    const newItem: RecentItem = {
      ...result,
      timestamp: Date.now(),
    };

    setRecentItems((prev) => {
      // Remove duplicates and add new item at the beginning
      const filtered = prev.filter(
        (item) => !(item.id === result.id && item.type === result.type)
      );
      const updated = [newItem, ...filtered];

      // Save to localStorage
      try {
        localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error("Error saving recent items:", error);
      }

      return updated;
    });
  };

  const removeRecentItem = (id: number, type: RecentItem["type"]) => {
    setRecentItems((prev) => {
      const updated = prev.filter(
        (item) => !(item.id === id && item.type === type)
      );
      try {
        localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error("Error saving recent items:", error);
      }
      return updated;
    });
  };

  // Get recent data projects (max 3)
  const recentProjects = React.useMemo(() => {
    return recentItems
      .filter((item) => item.type === "data-project")
      .slice(0, 3);
  }, [recentItems]);

  // Get recent contacts (max 3)
  const recentContacts = React.useMemo(() => {
    return recentItems.filter((item) => item.type === "contact").slice(0, 3);
  }, [recentItems]);

  const filteredProjects = React.useMemo(() => {
    if (!dataProjects || !searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    return dataProjects.filter((project) =>
      project.name?.toLowerCase().includes(query)
    );
  }, [dataProjects, searchQuery]);

  // Search for contacts when search query changes
  useEffect(() => {
    const searchContacts = async () => {
      if (!searchQuery.trim() || !selectedSystemId) {
        setContacts([]);
        return;
      }

      setIsSearchingContacts(true);
      try {
        const results = await searchForContacts(
          selectedSystemId,
          searchQuery.trim()
        );
        setContacts(results);
      } catch (error) {
        console.error("Error searching for contacts:", error);
        setContacts([]);
      } finally {
        setIsSearchingContacts(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchContacts();
    }, 300); // Debounce search by 300ms

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, selectedSystemId]);

  // Combine and prioritize results: data projects first, then contacts
  const searchResults = React.useMemo((): SearchResult[] => {
    const results: SearchResult[] = [];
    // Add data projects first
    filteredProjects.forEach((project) => {
      results.push({
        id: project.id,
        name: project.name || "",
        type: "data-project",
      });
    });

    // Add contacts second
    contacts.forEach((contact) => {
      const displayName = `${contact.first_name} ${contact.last_name}${
        contact.company ? ` - ${contact.company}` : ""
      }`;
      results.push({
        id: contact.id,
        name: displayName,
        type: "contact",
        contact,
      });
    });

    return results;
  }, [filteredProjects, contacts]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setShowResults(value.trim().length > 0);
    setSelectedIndex(-1); // Reset selection when search changes
  };

  const handleResultSelect = (result: SearchResult) => {
    // Add to recent items
    addRecentItem(result);

    if (result.type === "data-project") {
      navigate(`/data-project/dashboard/${result.id}`);
    } else if (result.type === "contact" && result.contact) {
      setSelectedContactId(result.id);
      setIsContactsModalOpen(true);
    }
    setSearchQuery("");
    setShowResults(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!showResults || searchResults.length === 0) {
      if (event.key === "Escape") {
        setShowResults(false);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex < searchResults.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
        break;
      case "Enter":
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleResultSelect(searchResults[selectedIndex]);
        } else if (searchResults.length > 0) {
          handleResultSelect(searchResults[0]);
        }
        break;
      case "Escape":
        event.preventDefault();
        setShowResults(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  return (
    <SearchContainer ref={searchRef}>
      <SearchBox>
        <InputWrapper>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <Input
            placeholder={"Search for contacts, accounts, data projects ..."}
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
          />
        </InputWrapper>
        {(recentProjects.length > 0 || recentContacts.length > 0) && (
          <RecentItemsContainer>
            {recentProjects.map((item) => (
              <Chip
                key={`recent-project-${item.id}`}
                label={item.name}
                onClick={() => handleResultSelect(item)}
                onDelete={() => removeRecentItem(item.id, item.type)}
                size="small"
                color="primary"
                variant="outlined"
                clickable
              />
            ))}
            {recentContacts.map((item) => (
              <Chip
                key={`recent-contact-${item.id}`}
                label={item.name}
                onClick={() => handleResultSelect(item)}
                onDelete={() => removeRecentItem(item.id, item.type)}
                size="small"
                variant="outlined"
                clickable
              />
            ))}
          </RecentItemsContainer>
        )}
      </SearchBox>
      {showResults && searchResults.length > 0 && (
        <SearchResultsContainer>
          <List>
            {searchResults.map((result, index) => (
              <ListItem
                key={`${result.type}-${result.id}`}
                disablePadding
                ref={index === selectedIndex ? selectedItemRef : null}
                secondaryAction={
                  <Chip
                    label={
                      result.type === "data-project"
                        ? "Data Project"
                        : "Contact"
                    }
                    size="small"
                    color={
                      result.type === "data-project" ? "primary" : "default"
                    }
                  />
                }
              >
                <ListItemButton
                  onClick={() => handleResultSelect(result)}
                  selected={index === selectedIndex}
                >
                  <ListItemText primary={result.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </SearchResultsContainer>
      )}
      <SuggestedChangeDetailsModal
        open={isContactsModalOpen}
        onClose={() => setIsContactsModalOpen(false)}
        recordId={selectedContactId}
        batchId={null}
        onSuccess={(suggestedId) => {}}
        integratedSystemId={selectedSystemId}
      />
    </SearchContainer>
  );
};
